import { Input } from "@ster/ster-toolkit";
import { useEffect, useState } from "react";

type BonusInputProps = Omit<
  Parameters<typeof Input.Currency>[0],
  "value" | "onChange"
> & {
  value?: number;
  onChange?: (value: number) => void;
  budget: number;
  asNetto?: boolean;
};

const BaseBonusInput = ({ onChange, budget, ...props }: BonusInputProps) => (
  <Input.Currency
    decimalScale={0}
    onValueChange={({ floatValue }, { source }) =>
      // Ensure we only act when the user made a change, NOT when there is a property change
      source === "event" && onChange?.(floatValue ?? 0)
    }
    {...props}
    isAllowed={(values) => {
      const { floatValue } = values;
      return floatValue === undefined || floatValue <= budget;
    }}
    max={budget}
  />
);

const BonusAsNettoInput = ({
  value,
  budget,
  onChange,
  ...props
}: BonusInputProps) => {
  const inputValue = Number(value);
  const [nettoValue, setNettoValue] = useState(budget - inputValue);

  useEffect(() => {
    setNettoValue(budget - inputValue);
  }, [inputValue, budget]);

  const handleChange = (newNettoValue: number) => {
    const newBonus = budget - newNettoValue;
    setNettoValue(newNettoValue);
    onChange?.(newBonus);
  };

  return (
    <BaseBonusInput
      value={nettoValue}
      budget={budget}
      onChange={handleChange}
      {...props}
    />
  );
};

const BonusInput = ({ asNetto, ...props }: BonusInputProps) => {
  if (asNetto) {
    return <BonusAsNettoInput {...props} />;
  }

  return <BaseBonusInput {...props} />;
};

export default BonusInput;
